import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type JSX,
} from "react";
import { Mail } from "@/icons/lucide-compat";
import { cn } from "@openreel/ui";
import { StepShell } from "./StepShell";
import { CodeInput } from "./CodeInput";
import {
  FIELD_BASE,
  FIELD_FOCUS,
  FIELD_INPUT,
  FIELD_MOTION,
  FOCUS_RING,
  PILL_BASE,
  PILL_DARK,
  PILL_DIMMED,
  PILL_LIGHT,
  PILL_MOTION,
} from "./surface";

/** Rough shape check only. The server is the authority on deliverability. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The code is always six numeric digits, matching what the server mints. */
const CODE_LENGTH = 6;

/** How long the resend control stays parked after a code goes out. */
const RESEND_SECONDS = 30;

const REQUEST_ERROR = "We could not send a code. Please try again in a moment.";
const INVALID_CODE = "That code is not right. Check it, or request a new one.";
const EXPIRED_CODE = "That code expired. Request a new one and try again.";
const TOO_MANY = "Too many tries. Request a new code and try again.";
const NETWORK_ERROR = "We could not reach the server. Try again in a moment.";

type SignInPhase = "options" | "email" | "code";

export interface SignInScreenProps {
  /** Kicks off the Google OAuth redirect. */
  onGoogle: () => void | Promise<void>;
  /**
   * Emails a 6-digit code. Resolves the same way whether or not the address has
   * an account, so the screen can advance without learning anything about it.
   */
  onRequestOtp: (email: string) => Promise<void>;
  /**
   * Verifies a code. On success the auth store gains a session and this screen
   * is unmounted by the gate, so there is nothing to render on resolve. A wrong,
   * expired, or rate-limited code rejects, and the rejection is read for a
   * `status`/`code`/`kind` to phrase the message.
   */
  onVerifyOtp: (email: string, code: string) => Promise<void>;
}

function GoogleGlyph(): JSX.Element {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

/**
 * Turn whatever the verify call rejected with into one honest line, without
 * importing the auth error classes: reading `kind`/`status`/`code` off the
 * thrown value keeps this component free of the network layer. A wrong and an
 * expired code both come back as `invalid_code`, so they share wording unless
 * the server is specific.
 */
function verifyErrorMessage(thrown: unknown): string {
  const e = thrown as { kind?: string; status?: number; code?: string | null };
  if (e?.kind === "network") return NETWORK_ERROR;
  if (e?.status === 429 || e?.code === "too_many_attempts" || e?.code === "rate_limited") {
    return TOO_MANY;
  }
  if (e?.code === "expired_code" || e?.code === "code_expired") return EXPIRED_CODE;
  return INVALID_CODE;
}

export function SignInScreen({
  onGoogle,
  onRequestOtp,
  onVerifyOtp,
}: SignInScreenProps): JSX.Element {
  const [phase, setPhase] = useState<SignInPhase>("options");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const trimmedEmail = email.trim();
  const emailLooksValid = EMAIL_PATTERN.test(trimmedEmail);
  const codeComplete = code.length === CODE_LENGTH;

  // One ticker runs while the code step is up, counting the resend cooldown down
  // to zero and then idling there until the next send resets it.
  useEffect(() => {
    if (phase !== "code") return;
    const timer = setInterval(() => {
      setResendIn((seconds) => (seconds > 0 ? seconds - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [phase]);

  const submitEmail = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (busy || !emailLooksValid) return;

      setBusy(true);
      setError(null);
      try {
        await onRequestOtp(trimmedEmail);
        // The confirmation is identical for known and unknown addresses.
        setCode("");
        setResent(false);
        setResendIn(RESEND_SECONDS);
        setPhase("code");
      } catch (thrown) {
        setError(
          (thrown as { kind?: string })?.kind === "network"
            ? NETWORK_ERROR
            : REQUEST_ERROR,
        );
      } finally {
        setBusy(false);
      }
    },
    [busy, emailLooksValid, onRequestOtp, trimmedEmail],
  );

  const submitCode = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (busy || !codeComplete) return;

      setBusy(true);
      setError(null);
      try {
        await onVerifyOtp(trimmedEmail, code);
        // On success the gate swaps this screen out; nothing to do here.
      } catch (thrown) {
        setError(verifyErrorMessage(thrown));
      } finally {
        setBusy(false);
      }
    },
    [busy, code, codeComplete, onVerifyOtp, trimmedEmail],
  );

  const resend = useCallback(async () => {
    if (resending || resendIn > 0) return;
    setResending(true);
    setError(null);
    setResent(false);
    try {
      await onRequestOtp(trimmedEmail);
      setCode("");
      setResent(true);
      setResendIn(RESEND_SECONDS);
    } catch (thrown) {
      setError(
        (thrown as { kind?: string })?.kind === "network"
          ? NETWORK_ERROR
          : REQUEST_ERROR,
      );
    } finally {
      setResending(false);
    }
  }, [onRequestOtp, resendIn, resending, trimmedEmail]);

  const backToEmail = useCallback(() => {
    setPhase("email");
    setCode("");
    setResent(false);
    setError(null);
  }, []);

  if (phase === "code") {
    return (
      <StepShell
        heading="Enter the code"
        subheading={`We emailed a ${CODE_LENGTH}-digit code to ${trimmedEmail}. It expires in 10 minutes.`}
        announcement={`Enter the ${CODE_LENGTH}-digit code we emailed to ${trimmedEmail}.`}
        error={error}
        footer={
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => void resend()}
                disabled={resending || resendIn > 0}
                className={cn(
                  PILL_BASE,
                  FOCUS_RING,
                  resending || resendIn > 0
                    ? PILL_DIMMED
                    : cn(PILL_MOTION, PILL_DARK),
                )}
              >
                {resendIn > 0
                  ? `Resend in ${resendIn}s`
                  : resending
                    ? "Sending..."
                    : "Resend code"}
              </button>

              <button
                type="button"
                onClick={backToEmail}
                className={cn(
                  "text-[14px] text-[#8f8f8f] underline-offset-4 hover:text-white hover:underline",
                  FOCUS_RING,
                  "rounded-full px-2 py-1",
                )}
              >
                Use a different email
              </button>
            </div>

            {resent ? (
              <p role="status" className="text-[13px] text-[#8f8f8f]">
                A new code is on its way.
              </p>
            ) : null}
          </div>
        }
      >
        <form
          onSubmit={submitCode}
          className="flex w-full flex-col items-center gap-4"
        >
          <div className="w-full max-w-sm">
            <CodeInput
              value={code}
              onChange={setCode}
              length={CODE_LENGTH}
              disabled={busy}
              invalid={Boolean(error)}
              autoFocus
              aria-label="Verification code"
            />
          </div>

          <button
            type="submit"
            disabled={!codeComplete || busy}
            className={cn(
              PILL_BASE,
              FOCUS_RING,
              "w-full max-w-xs",
              !codeComplete || busy ? PILL_DIMMED : cn(PILL_MOTION, PILL_LIGHT),
            )}
          >
            {busy ? "Verifying..." : "Verify"}
          </button>
        </form>
      </StepShell>
    );
  }

  if (phase === "email") {
    return (
      <StepShell
        heading="Let's make something"
        announcement="Enter your email and we'll send you a code."
        error={error}
        footer={
          <button
            type="button"
            onClick={() => {
              setPhase("options");
              setError(null);
            }}
            className={cn(
              "text-[14px] text-[#8f8f8f] underline-offset-4 hover:text-white hover:underline",
              FOCUS_RING,
              "rounded-full px-2 py-1",
            )}
          >
            Other sign-in options
          </button>
        }
      >
        <form
          onSubmit={submitEmail}
          className="flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center"
        >
          <label
            className={cn(
              FIELD_BASE,
              FIELD_MOTION,
              FIELD_FOCUS,
              "flex max-w-sm cursor-text items-center gap-2.5 text-[#8f8f8f]",
            )}
          >
            <Mail size={16} strokeWidth={1.5} aria-hidden />
            <span className="sr-only">Email address</span>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@studio.com"
              autoFocus
              autoComplete="email"
              spellCheck={false}
              className={FIELD_INPUT}
            />
          </label>

          <button
            type="submit"
            disabled={!emailLooksValid || busy}
            className={cn(
              PILL_BASE,
              FOCUS_RING,
              "w-full max-w-sm sm:w-auto",
              !emailLooksValid || busy ? PILL_DIMMED : cn(PILL_MOTION, PILL_LIGHT),
            )}
          >
            {busy ? "Sending..." : "Continue with email"}
          </button>
        </form>
      </StepShell>
    );
  }

  return (
    <StepShell
      heading="Let's make something"
      announcement="Sign in to continue."
      error={error}
      footer={
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => void onGoogle()}
            className={cn(PILL_BASE, PILL_MOTION, FOCUS_RING, PILL_LIGHT)}
          >
            <GoogleGlyph />
            <span>Continue with Google</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setPhase("email");
              setError(null);
            }}
            className={cn(PILL_BASE, PILL_MOTION, FOCUS_RING, PILL_DARK)}
          >
            <Mail size={16} strokeWidth={1.5} aria-hidden />
            <span>Continue with email</span>
          </button>
        </div>
      }
    />
  );
}
